import { useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeftRight,
  BarChart3,
  Boxes,
  Camera,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  PackagePlus,
  RotateCcw,
  ScanBarcode,
  Search,
  Store,
  Truck,
  Upload,
  UsersRound
} from "lucide-react";
import "./InventoryV1.css";

const INVENTORY_TABS = [
  { key: "home", label: "Inventory Home", icon: Boxes },
  { key: "products", label: "Products", icon: ScanBarcode },
  { key: "stock", label: "Stock", icon: Store },
  { key: "purchase", label: "Purchase", icon: PackagePlus },
  { key: "purchaseReturn", label: "Purchase Return", icon: RotateCcw },
  { key: "suppliers", label: "Suppliers", icon: UsersRound },
  { key: "transfer", label: "Transfer", icon: ArrowLeftRight },
  { key: "audit", label: "Audit", icon: ClipboardCheck },
  { key: "intelligence", label: "Purchase Intelligence", icon: BarChart3 }
];

const productRows = [
  { name: "Frame master", detail: "Brand, model, colour, size, MRP, barcode rules", count: "Setup pending", tone: "green" },
  { name: "Lens master", detail: "Lens type, index, coating, power range, HSN/SAC", count: "Ready for mapping", tone: "blue" },
  { name: "Contact lens", detail: "Brand, modality, power, batch and expiry", count: "UI only", tone: "amber" },
  { name: "Solutions/accessories", detail: "Solution, cloth, cleaner, case and add-ons", count: "UI only", tone: "purple" }
];

const stockCards = [
  { label: "Store-wise available", value: "0", text: "Will show live stock after posting is connected." },
  { label: "Low stock alerts", value: "0", text: "Reorder alerts will appear here later." },
  { label: "Barcode trace", value: "Ready", text: "Scan/search UI is prepared for future stock movement." }
];

const supplierRows = [
  { name: "Supplier master", detail: "GSTIN, contact, payment terms and active status", status: "Reference only" },
  { name: "Supplier payable", detail: "Disabled until proper purchase ledger is connected", status: "Coming soon" },
  { name: "GST input view", detail: "Monthly ITC view will come from posted purchases", status: "Coming soon" }
];

function openPurchaseV1() {
  const url = new URL(window.location.href);
  url.searchParams.set("screen", "purchaseV1");
  window.location.href = url.toString();
}

function openCnTracking() {
  const url = new URL(window.location.href);
  url.searchParams.set("screen", "cnTracking");
  window.location.href = url.toString();
}

function InventoryStat({ label, value, helper, tone = "green" }) {
  return (
    <article className={`iv1-stat iv1-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{helper}</small>
    </article>
  );
}

function SectionCard({ title, subtitle, children, action }) {
  return (
    <section className="iv1-card">
      <div className="iv1-card-head">
        <div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function PillButton({ children, onClick, variant = "soft", icon: Icon }) {
  return (
    <button className={`iv1-pill iv1-pill-${variant}`} type="button" onClick={onClick}>
      {Icon && <Icon size={17} />}
      <span>{children}</span>
    </button>
  );
}

function InventoryHome({ setActiveTab }) {
  return (
    <div className="iv1-view">
      <section className="iv1-hero">
        <div>
          <p className="iv1-kicker">Framesbay Optical CRM</p>
          <h1>Inventory cockpit</h1>
          <p>Product master, stock, purchase links, transfer, audit and intelligence in one clean inventory layer.</p>
        </div>
        <div className="iv1-search">
          <Search size={18} />
          <input placeholder="Search product, barcode, supplier, store" />
        </div>
      </section>

      <div className="iv1-quick-grid">
        <button type="button" onClick={() => setActiveTab("products")}>
          <ScanBarcode size={22} />
          <span>Products</span>
        </button>
        <button type="button" onClick={() => setActiveTab("stock")}>
          <Store size={22} />
          <span>Stock</span>
        </button>
        <button type="button" onClick={() => setActiveTab("purchase")}>
          <PackagePlus size={22} />
          <span>Purchase</span>
        </button>
        <button type="button" onClick={() => setActiveTab("audit")}>
          <ClipboardCheck size={22} />
          <span>Audit</span>
        </button>
      </div>

      <div className="iv1-stat-grid">
        <InventoryStat label="Available stock" value="0" helper="Will become live after stock posting" />
        <InventoryStat label="Low stock" value="0" helper="Alerts placeholder" tone="amber" />
        <InventoryStat label="Transfers" value="0" helper="Pending transfers placeholder" tone="blue" />
        <InventoryStat label="Audit issues" value="0" helper="Audit placeholder" tone="red" />
      </div>

      <div className="iv1-two-col">
        <SectionCard title="Recently viewed" subtitle="Quick reference area for product and barcode lookups.">
          <div className="iv1-row-list">
            <div className="iv1-row">
              <div>
                <strong>No live products connected yet</strong>
                <span>InventoryV1 is UI-only for now.</span>
              </div>
              <em>Preview</em>
            </div>
          </div>
        </SectionCard>
        <SectionCard title="Needs attention" subtitle="This will show low stock, mismatches and transfer delays later.">
          <div className="iv1-alert">
            <AlertTriangle size={20} />
            <div>
              <strong>No inventory posting yet</strong>
              <span>Stock changes are not saved from this screen.</span>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

function ProductsView() {
  return (
    <div className="iv1-view">
      <SectionCard
        title="Products"
        subtitle="Master areas for frames, sunglasses, lenses, contact lenses, solutions, accessories and non-chargeable items."
        action={<PillButton icon={PackagePlus}>Add product later</PillButton>}
      >
        <div className="iv1-product-grid">
          {productRows.map((product) => (
            <article className="iv1-product-card" key={product.name}>
              <div className={`iv1-product-icon iv1-${product.tone}`}>
                <ScanBarcode size={22} />
              </div>
              <div>
                <h4>{product.name}</h4>
                <p>{product.detail}</p>
              </div>
              <span>{product.count}</span>
            </article>
          ))}
        </div>
      </SectionCard>
      <SectionCard title="Product groups" subtitle="Same structure as the approved inventory design, kept as UI only.">
        <div className="iv1-chip-grid">
          {["Frame", "Sunglasses", "Lens", "Contact Lens", "Solution", "Other", "Non-Chargeable"].map((item) => (
            <button key={item} type="button">{item}</button>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

function StockView() {
  return (
    <div className="iv1-view">
      <div className="iv1-stat-grid">
        {stockCards.map((item) => (
          <InventoryStat key={item.label} label={item.label} value={item.value} helper={item.text} tone="blue" />
        ))}
      </div>
      <SectionCard title="Store-wise stock" subtitle="This will become the live store-wise stock table after inventory posting is connected.">
        <div className="iv1-table-card">
          <div className="iv1-table-row iv1-table-head">
            <span>Store</span>
            <span>Frames</span>
            <span>Lenses</span>
            <span>Alerts</span>
          </div>
          {["Sector 120", "Sector 137", "Crossings Republik", "Paras Tierra"].map((store) => (
            <div className="iv1-table-row" key={store}>
              <span>{store}</span>
              <span>0</span>
              <span>0</span>
              <span>Not connected</span>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

function PurchaseView() {
  return (
    <div className="iv1-view">
      <section className="iv1-purchase-banner">
        <div>
          <p className="iv1-kicker">Purchase bridge</p>
          <h2>Use PurchaseV1 for purchase entry</h2>
          <p>This inventory screen does not rebuild purchase entry. It only links to the existing PurchaseV1 page.</p>
        </div>
        <PillButton variant="dark" icon={PackagePlus} onClick={openPurchaseV1}>Open Purchase Entry</PillButton>
      </section>

      <div className="iv1-two-col">
        <SectionCard title="Invoice intake" subtitle="Invoice camera/upload scope stays visible as UI placeholder.">
          <div className="iv1-action-stack">
            <PillButton icon={Camera}>Scan invoice using camera</PillButton>
            <PillButton icon={Upload}>Upload invoice PDF/JPG/PNG</PillButton>
            <PillButton icon={FileText}>View original invoice</PillButton>
          </div>
        </SectionCard>
        <SectionCard title="Posting summary" subtitle="No stock/GST/vendor payable posting happens from InventoryV1.">
          <div className="iv1-row-list">
            <div className="iv1-row">
              <div>
                <strong>Bill to stock</strong>
                <span>Will be connected later.</span>
              </div>
              <em>Off</em>
            </div>
            <div className="iv1-row">
              <div>
                <strong>GST input</strong>
                <span>Will come from posted purchases later.</span>
              </div>
              <em>Off</em>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

function PlaceholderFlow({ title, subtitle, icon: Icon, points }) {
  return (
    <div className="iv1-view">
      <SectionCard title={title} subtitle={subtitle}>
        <div className="iv1-placeholder">
          <Icon size={36} />
          <h3>{title}</h3>
          <p>{subtitle}</p>
          <div className="iv1-step-list">
            {points.map((point, index) => (
              <div className="iv1-step" key={point}>
                <span>{index + 1}</span>
                <p>{point}</p>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

function SuppliersView() {
  return (
    <div className="iv1-view">
      <SectionCard title="Suppliers" subtitle="Supplier reference area for future purchase and payable workflows.">
        <div className="iv1-row-list">
          {supplierRows.map((row) => (
            <div className="iv1-row" key={row.name}>
              <div>
                <strong>{row.name}</strong>
                <span>{row.detail}</span>
              </div>
              <em>{row.status}</em>
            </div>
          ))}
        </div>
      </SectionCard>
      <div className="iv1-note">
        Accounting ledger is disabled until proper vendor ledger is built.
      </div>
    </div>
  );
}

function IntelligenceView() {
  return (
    <div className="iv1-view">
      <div className="iv1-stat-grid">
        <InventoryStat label="Fast moving" value="-" helper="After sales + stock connection" tone="green" />
        <InventoryStat label="Dead stock" value="-" helper="After stock ageing connection" tone="red" />
        <InventoryStat label="Reorder value" value="-" helper="After purchase history connection" tone="amber" />
      </div>
      <SectionCard title="Purchase intelligence" subtitle="Designed for future supplier, purchase, margin, stock ageing and reorder insights.">
        <div className="iv1-placeholder compact">
          <BarChart3 size={32} />
          <h3>Analysis layer ready</h3>
          <p>No live purchase or stock calculation is done from this UI preview.</p>
        </div>
      </SectionCard>
    </div>
  );
}

export default function InventoryV1() {
  const [activeTab, setActiveTab] = useState("home");
  const activeMeta = useMemo(() => INVENTORY_TABS.find((tab) => tab.key === activeTab) || INVENTORY_TABS[0], [activeTab]);
  const handleTabClick = (key) => {
    if (key === "purchaseReturn") {
      openCnTracking();
      return;
    }
    setActiveTab(key);
  };

  return (
    <div className="iv1-app">
      <aside className="iv1-side" aria-label="Inventory sections">
        <div className="iv1-brand">
          <Boxes size={25} />
          <div>
            <strong>InventoryV1</strong>
            <span>UI preview only</span>
          </div>
        </div>
        <nav>
          {INVENTORY_TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} className={activeTab === key ? "active" : ""} type="button" onClick={() => handleTabClick(key)}>
              <Icon size={18} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </aside>

      <main className="iv1-main">
        <header className="iv1-top">
          <div>
            <p className="iv1-kicker">SparkingCircle Optical OS</p>
            <h2>{activeMeta.label}</h2>
          </div>
          <span className="iv1-safe-badge">No stock posting</span>
        </header>

        <div className="iv1-mobile-tabs" aria-label="Inventory mobile sections">
          {INVENTORY_TABS.map(({ key, label }) => (
            <button key={key} className={activeTab === key ? "active" : ""} type="button" onClick={() => handleTabClick(key)}>
              {label}
            </button>
          ))}
        </div>

        {activeTab === "home" && <InventoryHome setActiveTab={setActiveTab} />}
        {activeTab === "products" && <ProductsView />}
        {activeTab === "stock" && <StockView />}
        {activeTab === "purchase" && <PurchaseView />}
        {activeTab === "purchaseReturn" && (
          <PlaceholderFlow
            title="Purchase Return"
            subtitle="Placeholder only. Purchase return logic is not connected yet."
            icon={RotateCcw}
            points={["Select supplier and bill later", "Choose items to return later", "Keep CN/return posting separate later"]}
          />
        )}
        {activeTab === "suppliers" && <SuppliersView />}
        {activeTab === "transfer" && (
          <PlaceholderFlow
            title="Stock Transfer"
            subtitle="Placeholder only. No store-to-store transfer is posted from this screen."
            icon={Truck}
            points={["Select source store later", "Scan/choose stock later", "Confirm receiving store later"]}
          />
        )}
        {activeTab === "audit" && (
          <PlaceholderFlow
            title="Stock Audit"
            subtitle="Placeholder only. No audit proof or stock variance is saved from this screen."
            icon={ClipboardCheck}
            points={["Start audit session later", "Scan physical stock later", "Review missing/damaged items later"]}
          />
        )}
        {activeTab === "intelligence" && <IntelligenceView />}
      </main>

      <div className="iv1-bottom">
        <button type="button" onClick={() => setActiveTab("home")}>
          <Boxes size={18} />
          Home
        </button>
        <button type="button" onClick={() => setActiveTab("purchase")}>
          <PackagePlus size={18} />
          Purchase
        </button>
        <button type="button" onClick={() => setActiveTab("stock")}>
          <CheckCircle2 size={18} />
          Stock
        </button>
      </div>
    </div>
  );
}
